import AddRounded from '@mui/icons-material/AddRounded';
import LocalShippingRounded from '@mui/icons-material/LocalShippingRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import SearchOffRounded from '@mui/icons-material/SearchOffRounded';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { useImageSrc } from '../../lib/images';
import { Money } from '../../components/Money';
import { EmptyState, PageHeader } from '../../components/states';
import { initials } from '../../lib/format';
import { ContactFormDialog } from './ContactFormDialog';
import { contactKeys, KIND, trimDecimal, type Contact, type ContactKind } from './model';
import { BalanceText } from './ui';
import { useExportSelection } from '../dataio/useExportSelection';
import { filesColumn, useAttachmentCounts } from '../attachments';

type ActiveFilter = 'true' | 'false' | 'all';

/** /customers and /suppliers: searchable, filterable list with balances. */
export function ContactListPage({ kind }: { kind: ContactKind }) {
  const exportSelection = useExportSelection('contact');
  const k = KIND[kind];
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const active = (params.get('active') ?? 'true') as ActiveFilter;
  const page = Number(params.get('page') ?? '1') || 1;
  const pageSize = Number(params.get('pageSize') ?? '25') || 25;
  const [creating, setCreating] = useState(false);
  // Phones: the balance shows its colour only; the meaning is on the detail page.
  const narrow = useMediaQuery(useTheme().breakpoints.down('sm'));

  const update = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [key, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(key);
      else p.set(key, v);
    }
    setParams(p, { replace: true });
  };

  const query = {
    type: kind,
    q: q || undefined,
    page,
    pageSize,
    isActive: active === 'all' ? undefined : active,
  };
  const list = useQuery({
    queryKey: contactKeys.list(query),
    queryFn: () => unwrap(api.GET('/contacts', { params: { query } })),
    placeholderData: keepPreviousData,
  });

  const balanceOf = (c: Contact) =>
    kind === 'customer' ? c.balances.receivable : c.balances.payable;

  const fileCounts = useAttachmentCounts(
    'CONTACT',

    (list.data?.data ?? []).map((r) => r.id),
  );

  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (c) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <ContactAvatar contact={c} />
          <Box sx={{ minWidth: 0, maxWidth: { xs: 130, sm: 260, md: 'none' } }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {c.name}
              </Typography>
              {kind === 'customer' && c.isSupplier && (
                <Chip
                  size="small"
                  label="Also supplier"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, display: { xs: 'none', sm: 'inline-flex' } }}
                />
              )}
              {kind === 'supplier' && c.isCustomer && (
                <Chip
                  size="small"
                  label="Also customer"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, display: { xs: 'none', sm: 'inline-flex' } }}
                />
              )}
              {c.isWalkIn && (
                <Chip
                  size="small"
                  label="Walk-in"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, display: { xs: 'none', sm: 'inline-flex' } }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {[c.phone, c.email].filter(Boolean).join(' · ') || 'No contact details'}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      key: 'terms',
      header: 'Terms',
      hideBelow: 'lg',
      render: (c) =>
        c.paymentTermsDays === null ? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ) : c.paymentTermsDays === 0 ? (
          'On receipt'
        ) : (
          `${String(c.paymentTermsDays)} days`
        ),
    },
    ...(kind === 'customer'
      ? [
          {
            key: 'limit',
            header: 'Credit limit',
            align: 'right' as const,
            hideBelow: 'md' as const,
            render: (c: Contact) =>
              c.creditLimit === null ? (
                <Typography variant="body2" color="text.disabled">
                  No limit
                </Typography>
              ) : (
                <Money value={trimDecimal(c.creditLimit)} />
              ),
          },
        ]
      : []),
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (c) =>
        c.isActive ? (
          <Chip size="small" label="Active" color="success" variant="outlined" />
        ) : (
          <Chip size="small" label="Inactive" variant="outlined" />
        ),
    },
    {
      key: 'balance',
      header: kind === 'customer' ? 'Receivable' : 'Payable',
      align: 'right',
      render: (c) => <BalanceText balance={balanceOf(c)} dense={narrow} />,
    },
  ];

  const canCreate = can(`${k.perm}.create`);
  const filtered = q !== '' || active !== 'true';
  const Icon = kind === 'customer' ? PeopleAltRounded : LocalShippingRounded;

  return (
    <>
      <PageHeader
        title={k.title}
        subtitle={
          kind === 'customer'
            ? 'People and businesses you sell to, with what they owe you.'
            : 'Vendors you buy from, with what you owe them.'
        }
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setCreating(true)}
            >
              New {k.singular}
            </Button>
          )
        }
      />
      <>
        <DataTable
          {...exportSelection.table}
          columns={[...columns, ...filesColumn<Contact>(fileCounts)]}
          rows={list.data?.data}
          rowKey={(c) => c.id}
          loading={list.isLoading}
          page={list.data?.page}
          onPageChange={(p) => update({ page: String(p) })}
          onPageSizeChange={(s) => update({ pageSize: String(s), page: null })}
          onRowClick={(c) => void navigate(`${k.base}/${c.id}`)}
          toolbar={
            <>
              <SearchField
                value={q}
                onChange={(v) => update({ q: v, page: null })}
                placeholder="Search name, phone or email"
              />
              <Box sx={{ flex: 1 }} />
              <ToggleButtonGroup
                size="small"
                exclusive
                value={active}
                onChange={(_, v: ActiveFilter | null) =>
                  v && update({ active: v === 'true' ? null : v, page: null })
                }
                aria-label="Status filter"
              >
                <ToggleButton value="true">Active</ToggleButton>
                <ToggleButton value="false">Inactive</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            </>
          }
          empty={
            filtered ? (
              <EmptyState
                icon={<SearchOffRounded />}
                title={`No ${k.title.toLowerCase()} match`}
                body="Try another name or phone number, or clear the filters."
                action={{
                  label: 'Clear filters',
                  onClick: () => update({ q: null, active: null, page: null }),
                }}
              />
            ) : (
              <EmptyState
                icon={<Icon />}
                title={`No ${k.title.toLowerCase()} yet`}
                body={
                  kind === 'customer'
                    ? 'Add your regular customers to sell on credit and track what they owe.'
                    : 'Add the suppliers you buy from to record purchases and what you owe.'
                }
                action={
                  canCreate
                    ? { label: `Add ${k.singular}`, onClick: () => setCreating(true) }
                    : undefined
                }
              />
            )
          }
        />
        {exportSelection.dialog}
      </>
      <ContactFormDialog
        open={creating}
        kind={kind}
        onClose={() => setCreating(false)}
        onSaved={(c) => void navigate(`${k.base}/${c.id}`)}
      />
    </>
  );
}

function ContactAvatar({ contact }: { contact: Contact }) {
  const { src } = useImageSrc(contact.imageUrl);
  return (
    <Avatar
      src={src ?? undefined}
      sx={(t) => {
        const p = (t.vars ?? t).palette.primary.main;
        return {
          width: 36,
          height: 36,
          fontSize: '0.8125rem',
          color: p,
          bgcolor: `color-mix(in srgb, ${p} 12%, transparent)`,
        };
      }}
    >
      {initials(contact.name)}
    </Avatar>
  );
}
